import { useState, useEffect } from "react";
import { Table, Empty, Modal, Form, Input, Select, DatePicker, Button, Tag, Typography, TreeSelect, Tooltip, Row, Col, Checkbox } from "antd";
import dayjs from "dayjs";
import { TrainingAPI } from "../../api/TrainingAPI";
import "../../Styles/styles-InjuryTable.css";
import { formattedPlayerName } from "../../Utils/name_utils";
import { useSelector } from "react-redux";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import "../../Styles/styles-TreatmentLog.css";
import { 
  PermissionedButton, 
  PermissionedPopConfirm,
  useUserPermissions
} from "../PermissionedComponents";
import PermissionViewable from "../PermissionedComponents/PermissionViewable";
import PieChart from "../Charts/PieChart";
import {PlayerOptions} from "@components/Common/PlayerOptions";


const getFormattedPlayerName = (teamName, player) => {
  return `${formattedPlayerName(teamName,`${player.first_name} ${player.last_name}`)} #${player.number}`
}

function exportToCsv(filename, rows, cols) {
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    const needsQuotes = /[",\n\r]/.test(s);
    const escaped = s.replace(/"/g, '""');
    return needsQuotes ? `"${escaped}"` : escaped;
  };

  const getValue = (row, dataIndex) => {
    if (!dataIndex) return "";
    if (Array.isArray(dataIndex)) {
      return dataIndex.reduce((acc, k) => (acc ? acc[k] : undefined), row);
    }
    return row[dataIndex];
  };

  const exportCols = (cols || []).filter((c) => c && c.title);

  const header = exportCols.map((c) => escape(c.title)).join(",");

  const body = (rows || [])
    .map((row) =>
      exportCols
        .map((c) => {
          // 1) use explicit export renderer if provided
          if (typeof c.exportRender === "function") {
            const raw = getValue(row, c.dataIndex);
            return escape(c.exportRender(raw, row));
          }

          // 2) fallback to plain value
          return escape(getValue(row, c.dataIndex));
        })
        .join(",")
    )
    .join("\n");

  const csv = `${header}\n${body}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  message.success("File downloaded successfully.");
}

// Helper functions for pie chart data calculation
const calculateTreatmentDistribution = (treatmentLogs, treatmentTypes = []) => {
  const treatmentCounts = {};
  let totalTreatments = 0;

  treatmentLogs.forEach(log => {
    if (log.treatments && Array.isArray(log.treatments)) {
      log.treatments.forEach(treatment => {
        treatmentCounts[treatment] = (treatmentCounts[treatment] || 0) + 1;
        totalTreatments++;
      });
    }
  });

  return Object.entries(treatmentCounts).map(([treatment, count]) => {
    const treatmentType = (Array.isArray(treatmentTypes) ? treatmentTypes : []).find(type => type.name === treatment);
    return {
      name: treatment,
      y: count,
      color: treatmentType?.color || '#CCCCCC'
    };
  });
};

const calculateLocationDistribution = (treatmentLogs, teamColors = []) => {
  const locationCounts = {};

  treatmentLogs.forEach(log => {
    if (log.location && Array.isArray(log.location)) {
      log.location.forEach(location => {
        locationCounts[location] = (locationCounts[location] || 0) + 1;
      });
    }
  });

  const defaultColors = ['#89f5ff', '#8884d8', '#a7ff81', '#ffaea3', '#4797ff', '#f526fA'];
  // Only use first 5 team colors to avoid dummy 6th item
  const validTeamColors = teamColors.length > 0 ? teamColors.slice(0, 5) : [];
  const colorsToUse = validTeamColors.length > 0 ? validTeamColors : defaultColors;

  return Object.entries(locationCounts).map(([location, count], index) => ({
    name: location,
    y: count,
    color: colorsToUse[index % colorsToUse.length]
  }));
};

const calculateTreatmentTimeDistribution = (treatmentLogs, teamColors = []) => {
  const timeCounts = {};

  treatmentLogs.forEach(log => {
    const time = log.treatment_application_time || 'Unknown';
    timeCounts[time] = (timeCounts[time] || 0) + 1;
  });

  const defaultColors = ['#89f5ff', '#8884d8', '#a7ff81', '#ffaea3', '#4797ff', '#f526fA'];
  // Only use first 5 team colors to avoid dummy 6th item
  const validTeamColors = teamColors.length > 0 ? teamColors.slice(0, 5) : [];
  const colorsToUse = validTeamColors.length > 0 ? validTeamColors : defaultColors;

  return Object.entries(timeCounts).map(([time, count], index) => ({
    name: time,
    y: count,
    color: colorsToUse[index % colorsToUse.length]
  }));
};

const TreatmentInputForm = (props) => {
	const {
		misc: { 
            treatment_types,
            injury_location_tree,
            treatment_applied_time,
        },
        players,
        info: { display_name: teamName},
        playersStatus
	} = useSelector(({ team }) => team);

  const [form] = Form.useForm();
  const mode = props?.inputData?.id ? "edit" : "add";
  const log_id = props?.inputData?.treatment_log_id || null;
  const trainingAPI = new TrainingAPI();

  useEffect(() => {
    form.setFieldsValue({
      date: props.inputData?.date ? dayjs(props.inputData.date) : null,
      player_name: props.inputData?.player || null,
      treatments: props.inputData?.treatments || [],
      location: props.inputData?.location || [],
      treatment_application_time: props.inputData?.treatment_application_time || null,
      notes: props.inputData?.notes || ""
    });
  }, [props.inputData]);


  const uniqueLocationMap = {};
  (injury_location_tree || []).forEach(item => {
    const existing = uniqueLocationMap[item.key];
    if (!existing || (item.children || []).length > (existing.children || []).length) {
      uniqueLocationMap[item.key] = item;
    }
  });
  const modifiedInjuryLocationTree = Object.values(uniqueLocationMap).map(item => {
    const modified = { value: item.key, title: item.key };
    if (item.children && item.children.length > 0) {
      modified.children = item.children.map(child => ({ value: child.key, title: child.key }));
    }
    return modified;
  });


  const onSubmit = (values) => {

    const payload = {
      ...values,
        date: values.date?.format("YYYY-MM-DD"),
        player: values.player_name,
    }

    if (mode === "add"){
      trainingAPI.addTreatmentLog(payload)
      .then(() => {
          message.success("Treatment log added successfully");
          props?.setIsFormOpen(false);
      })
      .catch((error) => {
          console.log(error);
          message.error("Error adding treatment log");
      });
    } else if (mode === "edit"){
      trainingAPI.updateTreatmentLog(log_id, payload)
      .then(() => {
          message.success("Treatment log added successfully");
          props?.setIsFormOpen(false);
      })
      .catch((error) => {
          console.log(error);
          message.error("Error adding treatment log");
      });
    } else {
      message.error("Unknown action");
    }
  }

  return (
  <>

        <Form
            form={form}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 20 }}
            layout="horizontal"
            style={{ maxWidth: 600 }}
            onFinish={onSubmit}
      >

        <Form.Item 
            name="date" 
            label="Date" 
            rules={[{ required: true, message: "Please select a date" }]}
            >
            <DatePicker format="YYYY-MM-DD" style={{ borderRadius: "5px" }} />
        </Form.Item>
        <Form.Item 
            name="player_name" 
            label="Player" 
            rules={[{ required: true, message: "Please select a player" }]}
            >
            <Select
                showSearch
                allowClear
                style={{ width: 250 }}
                placeholder="Choose Player"
                filterOption={(input, option) =>
                    option?.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
            >
                {players?.filter(item => item.label !== "Unknown")
                .map((player, i) => {
                  return PlayerOptions({ player, teamName, index: i, playersStatus });
                })}  
            </Select>
            </Form.Item>


            <Form.Item 
                name="treatments" 
                label="Treatments" 
                rules={[{ required: true, message: "Please select at least one treatment" }]}
                >
                <Select
                    showSearch
                    mode="multiple"
                    optionFilterProp="label"
                    allowClear
                    placeholder="Choose Treatments"
                    style={{ width: 250 }}
                    options={(Array.isArray(treatment_types) ? treatment_types : []).map((treatment) => ({
                      label: treatment.name,
                      value: treatment.name,
                    }))}
                    filterOption={(input, option) =>option?.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                />
            </Form.Item>

            <Form.Item 
                name="location" 
                label="Location" 
                rules={[{ required: true, message: "Please select a location" }]}
                >
              <TreeSelect
                showSearch
                treeData={modifiedInjuryLocationTree}
                style={{ width: 250 }}
                multiple
                virtual={false}
                placeholder="Please select"
                allowClear
                />

            </Form.Item>

            <Form.Item 
                name="treatment_application_time" 
                label="When Applied" 
                rules={[{ required: true, message: "Please select when the treatment was applied" }]}
                >
                <Select
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    placeholder="When Applied"
                    style={{ width: 250 }}
                    options={(Array.isArray(treatment_applied_time) ? treatment_applied_time : []).map((treatment) => ({
                    label: (
                        <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {treatment.name}
                        </span>
                    ),
                    value: treatment.name,
                    }))}
                    filterOption={(input, option) =>option?.value?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                />
            </Form.Item>

            <Form.Item 
                name="notes" 
                label="Notes"
                >
                <Input.TextArea 
                    rows={4}
                    placeholder="Enter any relevant notes"
                    style={{ borderRadius: "5px" }}
                />
            </Form.Item>

            <Form.Item>
                <PermissionedButton 
                    type="primary" 
                    htmlType="submit"
                    permissionCheck={mode === "add" ? "AddTreatmentLogs" : "EditTreatmentLogs"}
                >
                Save Treatments
                </PermissionedButton>
            </Form.Item>

      </Form>
  </>
  )
}

const BulkTreatmentModal = ({ open, onClose, onSaved }) => {
  const {
    misc: { treatment_types, injury_location_tree, treatment_applied_time },
    players,
    info: { display_name: teamName },
  } = useSelector(({ team }) => team);

  const trainingAPI = new TrainingAPI();

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedWhenApplied, setSelectedWhenApplied] = useState(null);
  const [notes, setNotes] = useState('');
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  const [bulkData, setBulkData] = useState([]);
  const [userChecked, setUserChecked] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');

  const activePlayers = (players || []).filter(p => p.label !== 'Unknown');

  const uniqueLocMap = {};
  (injury_location_tree || []).forEach(item => {
    const existing = uniqueLocMap[item.key];
    if (!existing || (item.children || []).length > (existing.children || []).length) {
      uniqueLocMap[item.key] = item;
    }
  });
  const locationTreeData = Object.values(uniqueLocMap).map(item => {
    const node = { value: item.key, title: item.key };
    if (item.children && item.children.length > 0) {
      node.children = item.children.map(child => ({ value: child.key, title: child.key }));
    }
    return node;
  });

  // Derive existingMap with strict three-way matching on (date, location, when-applied).
  // A blank selection means "saved with no value" — not "match any value".
  const existingMap = {};
  (bulkData || []).forEach(log => {
    const logLocations = Array.isArray(log.location)
      ? log.location
      : (log.location ? [log.location] : []);

    if (selectedLocations.length > 0) {
      if (!selectedLocations.every(loc => logLocations.includes(loc))) return;
    } else {
      if (logLocations.length > 0) return;
    }

    const logWhenApplied = log.treatment_application_time ?? null;
    if (selectedWhenApplied !== logWhenApplied) return;

    if (!existingMap[log.player]) existingMap[log.player] = new Set();
    (log.treatments || []).forEach(t => existingMap[log.player].add(t));
  });

  const fetchBulkData = (date) => {
    setLoading(true);
    trainingAPI.getBulkTreatmentLogs({ date: date.format('YYYY-MM-DD') })
      .then((data) => {
        setBulkData(data || []);
        setUserChecked({});
      })
      .catch(() => message.error('Error fetching bulk treatment data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    const initialTreatments = (Array.isArray(treatment_types) ? treatment_types : [])
      .slice(0, 3)
      .map(t => t.name);
    setSelectedTreatments(initialTreatments);
    setSelectedLocations([]);
    setSelectedWhenApplied(null);
    setNotes('');
    setBulkData([]);
    setUserChecked({});
    setSearchText('');
    const today = dayjs();
    setSelectedDate(today);
    fetchBulkData(today);
  }, [open]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) fetchBulkData(date);
  };

  const handleLocationChange = (vals) => {
    setSelectedLocations(vals ?? []);
    setUserChecked({});
  };

  const handleWhenAppliedChange = (val) => {
    setSelectedWhenApplied(val ?? null);
    setUserChecked({});
  };

  const isChecked = (playerId, treatment) =>
    !!existingMap[playerId]?.has(treatment) || !!userChecked[playerId]?.[treatment];

  const handleCheckboxChange = (playerId, treatment, checked) => {
    setUserChecked(prev => ({
      ...prev,
      [playerId]: { ...(prev[playerId] || {}), [treatment]: checked },
    }));
  };

  const handleSave = () => {
    if (!selectedDate) {
      message.error('Please select a date');
      return;
    }

    const playerTreatments = activePlayers
      .map(player => ({
        player: player.player_id,
        treatments: selectedTreatments.filter(t => isChecked(player.player_id, t)),
      }))
      .filter(item => item.treatments.length > 0);

    if (playerTreatments.length === 0) {
      message.warning('Select at least one player treatment');
      return;
    }

    const payload = {
      date: selectedDate.format('YYYY-MM-DD'),
      location: selectedLocations,
      treatment_application_time: selectedWhenApplied,
      notes,
      player_treatments: playerTreatments,
    };

    setSaving(true);
    trainingAPI.saveBulkTreatmentLogs(payload)
      .then(() => {
        message.success('Bulk treatments applied successfully');
        onSaved?.();
        fetchBulkData(selectedDate);
      })
      .catch(() => message.error('Error applying bulk treatments'))
      .finally(() => setSaving(false));
  };

  // Include filter combo in the row key so Ant Design's rc-virtual-list
  // treats rows as new when location/whenApplied changes, forcing fresh cell renders.
  const filterKey = `${selectedLocations.join(',')}_${selectedWhenApplied ?? ''}`;
  const tableData = activePlayers.map(player => {
    const treatmentStates = {};
    selectedTreatments.forEach(t => {
      treatmentStates[t] = {
        checked: !!existingMap[player.player_id]?.has(t) || !!userChecked[player.player_id]?.[t],
        disabled: !!existingMap[player.player_id]?.has(t),
      };
    });
    return {
      key: `${player.player_id}_${filterKey}`,
      playerId: player.player_id,
      playerName: getFormattedPlayerName(teamName, player),
      treatmentStates,
    };
  });

  const filteredTableData = searchText.trim()
    ? tableData.filter(row =>
        row.playerName.toLowerCase().includes(searchText.trim().toLowerCase()))
    : tableData;

  const tableColumns = [
    {
      title: 'Player',
      key: 'player',
      width: 200,
      align: 'left',
      sorter: (a, b) => a.playerName.localeCompare(b.playerName),
      defaultSortOrder: 'ascend',
      onHeaderCell: () => ({
        style: { position: 'sticky', left: 0, zIndex: 3, background: '#fafafa' },
      }),
      onCell: () => ({
        style: { position: 'sticky', left: 0, zIndex: 1, background: '#fff' },
      }),
      render: (_, record) => record.playerName,
    },
    ...selectedTreatments.map(treatment => ({
      title: treatment,
      key: treatment,
      width: 130,
      align: 'center',
      render: (_, record) => (
        <Checkbox
          checked={record.treatmentStates[treatment]?.checked ?? false}
          disabled={record.treatmentStates[treatment]?.disabled ?? false}
          onChange={e => handleCheckboxChange(record.playerId, treatment, e.target.checked)}
        />
      ),
    })),
  ];

  return (
    <Modal
      title="Bulk Insert Treatments"
      open={open}
      onCancel={onClose}
      onClose={onClose}
      destroyOnHidden
      footer={null}
      width={1000}
    >
      <Form className="bulk-treatment-form" layout="horizontal" labelAlign="left" labelCol={{ style: { width: 110 } }} wrapperCol={{ style: { flex: 1 } }}>
        <Form.Item label="Date" required>
          <DatePicker
            value={selectedDate}
            format="YYYY-MM-DD"
            onChange={handleDateChange}
            style={{ borderRadius: 5 }}
          />
        </Form.Item>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label="Location">
              <TreeSelect
                showSearch
                allowClear
                multiple
                placeholder="Select Location"
                value={selectedLocations}
                treeData={locationTreeData}
                virtual={false}
                onChange={handleLocationChange}
                onClear={() => handleLocationChange([])}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="When Applied">
              <Select
                allowClear
                showSearch
                placeholder="When Applied"
                value={selectedWhenApplied}
                onChange={handleWhenAppliedChange}
                onClear={() => handleWhenAppliedChange(null)}
                options={(Array.isArray(treatment_applied_time) ? treatment_applied_time : []).map(t => ({
                  label: t.name,
                  value: t.name,
                }))}
                filterOption={(input, option) =>
                  option?.value?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Notes">
          <Input.TextArea
            rows={3}
            placeholder="Enter any relevant notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            style={{ borderRadius: 5 }}
          />
        </Form.Item>

        <Form.Item label="Treatments" required>
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            allowClear
            placeholder="Select Treatments"
            value={selectedTreatments}
            onChange={setSelectedTreatments}
            options={(Array.isArray(treatment_types) ? treatment_types : []).map(t => ({
              label: t.name,
              value: t.name,
            }))}
          />
        </Form.Item>
      </Form>

      <Input
        className="bulk-treatment-search"
        allowClear
        placeholder="Search player"
        prefix={<SearchOutlined />}
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
      />

      <div style={{ overflowX: 'auto' }} className="bulk-treatment-table">
        <Table
          loading={loading}
          columns={tableColumns}
          dataSource={filteredTableData}
          pagination={false}
          scroll={{ y: 260 }}
          size="small"
          bordered
        />
      </div>

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onClose}>Cancel</Button>
        <PermissionedButton
          type="primary"
          onClick={handleSave}
          loading={saving}
          permissionCheck="AddTreatmentLogs"
          disabled={selectedTreatments.length === 0}
        >
          Save
        </PermissionedButton>
      </div>
    </Modal>
  );
};

export const TreatmentLog = () => {

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [inputData, setInputData] = useState({});
  const [allTreatmentLogs, setAllTreatmentLogs] = useState([]);
  const [showChartLabels, setShowChartLabels] = useState(true);
  const trainingAPI = new TrainingAPI();
  const dateFormat = "YYYY-MM-DD";

  const [dateRange, setDateRange] = useState([
    dayjs().subtract(2, "week").format(dateFormat),
    dayjs().format(dateFormat),
    ]);

    const [filters, setFilters] = useState({});
  
    const userPermissions = useUserPermissions();
    const {
      players,
      positions,
      playerGroups,
      misc: { treatment_types },
      info,
      info: { display_name: teamName},
      playersStatus
    } = useSelector(({ team }) => team);

  useEffect(() => { getAllTreatmentLogs() }, [isFormOpen, userPermissions?.ViewTreatmentLogs, dateRange]);

  const getTreatmentColorByName = (treatment) => {
    const types = Array.isArray(treatment_types) ? treatment_types : [];
    const treatmentType = types.find((type) => type.name === treatment);
    if (treatmentType) {
      return treatmentType.color || "#CCCCCC"; // Default color if not specified
    }
    return "#CCCCCC"; // Default color for unknown treatments
  }

  const applyFilters = (treatmentLogs) => {
    const activeFilters = { ...filters };
  
    // Clean up empty filters
    Object.keys(activeFilters).forEach((key) => {
      if (!activeFilters[key] || activeFilters[key]?.length === 0) {
        delete activeFilters[key];
      }
    });
  
    if (Object.keys(activeFilters).length === 0) {
      return treatmentLogs;
    }
  
    return treatmentLogs
      .map((log) => {
        const player = (players || []).find((p) => p.player_id === log.player);
  
        // Player filter
        if (activeFilters.player && activeFilters.player !== log.player) return null;
  
        // Position filter
        if (
          activeFilters.positions?.length > 0 &&
          (!player || !activeFilters.positions.includes(player.position))
        ) {
          return null;
        }
  
        // Group filter
        if (activeFilters.groups?.length > 0) {
          const matchesGroup = player?.player_groups?.some(group =>
            activeFilters.groups.includes(group)
          );
          if (!matchesGroup) return null;
        }
  
        // Treatment filter
        let filteredTreatments = log.treatments || [];
        if (activeFilters.treatments?.length > 0) {
          filteredTreatments = (log.treatments || []).filter((t) =>
            activeFilters.treatments.includes(t)
          );
          if (filteredTreatments.length === 0) return null;
        }
  
        // Location filter
        let filteredLocations = log.location;
        if (activeFilters.locations?.length > 0) {
          filteredLocations = log.location?.filter((loc) =>
            activeFilters.locations.includes(loc)
          ) || [];
          if (filteredLocations.length === 0) return null;
        }

        // treatment_application_time filter
        let treatmentApplicationTime = log.treatment_application_time || "";
        if (activeFilters.treatment_application_time && !activeFilters.treatment_application_time.includes(treatmentApplicationTime)) {
          return null;
        }
  
        return {
          ...log,
          treatments: filteredTreatments,
          location: filteredLocations,
        };
      })
      .filter(Boolean);
  };
  
  

  const genTreatmentTree = (treatmentLogs) => {

    const filtered = applyFilters(treatmentLogs);

    // Calculate summary data
    const uniqueDates = [...new Set(filtered.map(log => log.date))];
    const uniquePlayers = [...new Set(filtered.map(log => log.player))];
    const allTreatments = filtered.flatMap(log => log.treatments || []);
    const uniqueTreatments = [...new Set(allTreatments)];
    const allLocations = filtered.flatMap(log => log.location || []);
    const uniqueLocations = [...new Set(allLocations)];
    const allWhenApplied = filtered.map(log => log.treatment_application_time || 'Unknown');
    const uniqueWhenApplied = [...new Set(allWhenApplied)];

    // Create summary row
    const summaryRow = {
      key: 'summary',
      date: <span style={{fontWeight: 600}}>Total ({uniqueDates.length})</span>,
      player: <span style={{fontWeight: 600}}>{uniquePlayers.length}</span>,
      treatment_count: <span style={{fontWeight: 600}}>{allTreatments.length} ({uniqueTreatments.length})</span>,
      formatted_treatment_list: <span style={{fontWeight: 600}}>{uniqueTreatments.length}</span>,
      formatted_locations: <span style={{fontWeight: 600}}>{uniqueLocations.length}</span>,
      treatment_application_time: <span style={{fontWeight: 600}}>{uniqueWhenApplied.length}</span>,
      notes: '',
      actions: '',
      total_count: allTreatments.length,
      unique_treatments_on_curr_date: uniqueTreatments,
      unique_locations_on_curr_date: uniqueLocations.length,
      raw_treatment_list: uniqueTreatments,
      raw_location_list: uniqueLocations,
    };

    const treatmentTree = uniqueDates.map(date => {
    const logsForDate = filtered.filter(log => log.date === date);

        let treatment_info_for_curr_day = {}

        const subData = logsForDate.map(log => {

            (log.treatments || []).forEach(treatment => {
                if (!treatment_info_for_curr_day[treatment]) {
                    treatment_info_for_curr_day[treatment] = {count: 0, locations: new Set()};
                }
                treatment_info_for_curr_day[treatment].count += 1;
                if (log.location) {
                  log.location.forEach(x => treatment_info_for_curr_day[treatment].locations.add(x));
                }
            })

            
            return {
                key: log.id,
                date: log.date,
                player_name: log.player_name,
                player: log.player_name,
                treatment_count: (log.treatments || []).length,
                
                raw_treatment_list: log.treatments || [],
                formatted_treatment_list: (log.treatments || []).map((treatment, idx) => (
                  <Tag className="treatment-tag" key={idx} style={{ margin: 2, backgroundColor: getTreatmentColorByName(treatment) }}>{treatment}</Tag>
                )),

                raw_location_list: log.location || [],
                unique_locations_on_curr_date: new Set(log.location || []).size,
                formatted_locations: (log.location || []).map((loc, idx) => (
                  <Tag key={idx} color="geekblue" style={{ margin: 2 }}> {loc} </Tag>
                )),

                treatment_application_time: log.treatment_application_time || "",
                location: log.location || "",
                notes: log.notes || "",
                actions: (
                  <div style={{ display: "flex", justifySelf:'center', gap: 4}}>
                    <PermissionedButton
                      icon={<EditOutlined />}
                      shape="circle"
                      type="primary"
                      size="small"
                      ghost
                      onClick={() => handleEdit(log.id)}
                      permissionCheck="EditTreatmentLogs"
                    />
        
                    <PermissionedPopConfirm
                        placement="bottom"
                        title="Are you sure you want to delete this treatment log?"
                        onConfirm={() => handleDelete(log.id)}
                        okText="Yes"
                        cancelText="No"
                        permissionCheck="DeleteTreatmentLogs"
                        >
                        <Button 
                            icon={<DeleteOutlined />} 
                            shape="circle" 
                            size="small"
                            danger 
                            ghost 
                        />                    
                    </PermissionedPopConfirm>
                  </div>
                )
            };
        });

        let player_count_for_current_date = new Set((subData || []).map(item => item.player_name)).size;
        let unique_treatments_on_curr_date = treatment_info_for_curr_day ? Object.keys(treatment_info_for_curr_day) : [];
        let total_treatments_on_curr_date = (subData || []).reduce((acc, item) => acc + (item?.treatment_count || 0), 0);

        let unique_locations_on_curr_date = []
        if (Object.keys(treatment_info_for_curr_day).length > 0) {
          let locations = Object.keys(treatment_info_for_curr_day)
            .map(treatment => treatment_info_for_curr_day[treatment]?.locations)
            .filter(Boolean);
          let all_locations = [];
          locations.forEach(loc => all_locations.push(...Array.from(loc)));
          unique_locations_on_curr_date = new Set(all_locations);
        }

        const treatment_count_with_tooltip =(
        <Tooltip title={<>Total: {total_treatments_on_curr_date} | Unique: {unique_treatments_on_curr_date?.length}</>}>
          {total_treatments_on_curr_date} ({unique_treatments_on_curr_date.length})
        </Tooltip>
      )


        return {
            key: date,
            date: date,
            player: player_count_for_current_date,
            treatment_count: treatment_count_with_tooltip,
            formatted_treatment_list: unique_treatments_on_curr_date.map((treatment, idx) => (
              <Tag className="treatment-tag" key={idx} style={{margin: 2, backgroundColor: getTreatmentColorByName(treatment)}}>
                {treatment} ({treatment_info_for_curr_day[treatment]?.count || 0})
              </Tag>
            )),
            formatted_locations: unique_locations_on_curr_date?.size || '-',
            unique_locations_on_curr_date: unique_locations_on_curr_date.size || 0,
            treatment_application_time: [...new Set((subData || []).map(item => item.treatment_application_time))].length,
            notes: "",
            actions: "",

            total_count: total_treatments_on_curr_date,

            subData: subData,
            unique_treatments_on_curr_date: unique_treatments_on_curr_date,
            children: subData
        };
    }
    );

    return [summaryRow, ...treatmentTree];
    }

  const getAllTreatmentLogs = () => {
    if (!userPermissions?.ViewTreatmentLogs) return;
    if (dateRange.some((date) => !date || !dayjs(date).isValid())) {
      console.warn("Skipped treatment log fetch: invalid date range", dateRange);
      return;
    }
    trainingAPI.getAllTreatmentLogs({start_date: dateRange[0], end_date: dateRange[1]})
      .then((data) => {
        setAllTreatmentLogs(data);
      }
      ).catch((error) => {
        console.log(error);
        message.error("Error fetching treatment logs");
      });
  }

  const deleteTreatmentLog = (logId) => {
    trainingAPI.deleteTreatmentLog(logId)
      .then(() => {
        message.success("Treatment log deleted successfully");
        getAllTreatmentLogs();
      })
      .catch((error) => {
        console.log(error);
        message.error("Error deleting treatment log");
      });
  }

  const getTreatmentLogById = (logId) => {
    return allTreatmentLogs.find((log) => log.id === logId);
  }

  const handleEdit = (logId) => {
    const logData = getTreatmentLogById(logId);
    if (logData) {
        setInputData({...logData, treatment_log_id: logId});
        setIsFormOpen(true);
    } else {
      message.error("Treatment log not found");
    }
  }

  const handleDelete = (logId) => {
    deleteTreatmentLog(logId);
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 150,
      sorter: {
        compare: (a, b) => new Date(a.date) - new Date(b.date),
        multiple: 1,
      },
    },
    {
      'title': 'Player',
      'dataIndex': 'player',
      'key': 'player',
      className: 'treatment-player-column',
      exportRender: (_, record) => record.player_name || '',
    },
    {
      title: "Treatment Count",
      dataIndex: "treatment_count",
      key: "treatment_count",
      width: 10,
      sorter: {
        compare: (a, b) => a.total_count - b.total_count,
        multiple: 1,
      },
      exportRender: (_, record) => (record.treatments || []).length,
    },
    {
      title: "Treatments",
      dataIndex: "formatted_treatment_list",
      key: "formatted_treatment_list",
      width: 200,
      sorter: {
        compare: (a, b) => (a.unique_treatments_on_curr_date?.length || 0) - (b.unique_treatments_on_curr_date?.length || 0),
        multiple: 1,
      },
      filters: (treatment_types || []).map((treatment) => ({
        text: treatment.name,
        value: treatment.name,
      })),
      onFilter: () => true, // required to show the filter UI, but doesn't do real filtering
      exportRender: (_, record) => (record.treatments || []).join(", "),
    },
      {
        'title': 'Location',
        'dataIndex': 'formatted_locations',
        'key': 'formatted_locations',
        onFilter: () => true, // just to make the UI appear — real filtering is handled in applyFilters
        filters: Array.from(new Set((allTreatmentLogs || []).flatMap(log => log?.location || []))).map(location => ({
          text: location,
          value: location,
        })),
        sorter: {
          compare: (a, b) => a.unique_locations_on_curr_date - b.unique_locations_on_curr_date,
          multiple: 1,
        },
        exportRender: (_, record) => (record.location || []).join(", "),
      },
      {
        'title': 'When Applied',
        'dataIndex': 'treatment_application_time',
        'key': 'treatment_application_time',
        onFilter: () => true, // just to make the UI appear — real filtering is handled in applyFilters
        filters: Array.from(new Set((allTreatmentLogs || []).map(log => log?.treatment_application_time || ''))).map(time => ({
          text: time,
          value: time,
        })),
        sorter: {
          compare: (a, b) => a?.treatment_application_time?.localeCompare(b?.treatment_application_time),
          multiple: 1,
        },
      exportRender: (_, record) => record.treatment_application_time || '',
      },
      {
        'title': 'Notes',
        'dataIndex': 'notes',
        'key': 'notes',
        render: (text) => (
          <Typography.Text 
            style={{ maxWidth: '100px' }}
            ellipsis={{ tooltip: text }}
          >
            {text}
          </Typography.Text>
        ),
        exportRender: (_, record) => record.notes || '',
      },
      {
        'title': 'Actions',
        'dataIndex': 'actions',
        'key': 'actions',
      },
      
  ];

  return (
    <div
      style={{
        margin: "0px 0px 24px",
      }}
    >
      <div style={{ display: "flex", gap: 16 }}>

      <PermissionedButton
        type="primary"
        onClick={() => setIsFormOpen(true)}
        permissionCheck="AddTreatmentLogs"
      >
        Add Treatment
      </PermissionedButton>
      <PermissionedButton
        onClick={() => setIsBulkOpen(true)}
        permissionCheck="AddTreatmentLogs"
      >
        Bulk Upload Treatments
      </PermissionedButton>
      <Button
        variant="outlined"
        onClick={() => {getAllTreatmentLogs(); message.success("Treatment logs refreshed")}}
        style={{right: 0, position: 'absolute'}}
        disabled={!userPermissions?.ViewTreatmentLogs}
        icon={<ReloadOutlined />}
        />
      </div>

      <Modal
        title="New Treatment"
        open={isFormOpen}
        onClose={() => {setInputData({}); setIsFormOpen(false)}}
        onCancel={() => {setInputData({}); setIsFormOpen(false)}}
        destroyOnHidden={true}
        footer={null}
        >
          <TreatmentInputForm
            setIsFormOpen={setIsFormOpen}
            inputData={inputData}
          />
        </Modal>

        <BulkTreatmentModal
          open={isBulkOpen}
          onClose={() => setIsBulkOpen(false)}
          onSaved={getAllTreatmentLogs}
        />
        {!userPermissions?.ViewTreatmentLogs? <div style={{marginTop: '10px'}}/>:null}
        <PermissionViewable 
        permissionCheck="ViewTreatmentLogs"
        permissionMessage="You don't have permission to view treatment logs"
      >
      <div className="health-filter-bar">

        <Typography.Text className="health-filter-bar-label">Filters:</Typography.Text>

        <DatePicker.RangePicker
            style={{ borderRadius: "5px", width: 250 }}
            format={dateFormat}
            allowClear={false}
            value={[
              dayjs(dateRange[0]),
              dayjs(dateRange[1]),
            ]}
            onChange={(_, dateStrings) => setDateRange(dateStrings)}
            />
        
        <Select
          showSearch
          allowClear
          style={{ width: 220, flexShrink: 0 }}
          placeholder="Choose Player"
          onChange={(value) => {setFilters({...filters, player: value})}}
          value={filters?.player || null}
          filterOption={(input, option) =>
              option?.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
      >
          {players?.filter(item => item.label !== "Unknown")
          .map((player, i) => {
            return PlayerOptions({ player, teamName, index: i, playersStatus});
          })}  
          </Select>

                  <Select
            showSearch
            allowClear
            placeholder="All Positions"
            style={{ width: 220, flexShrink: 0 }}
            options={(Array.isArray(positions) ? positions : []).map((position) => ({
              label: position,
              value: position,
            }))}
            onChange={(value) => {setFilters({...filters, positions: value})}}   
            mode="multiple"
            maxTagCount="responsive"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={filters?.positions || []}
          />
          <Select
            showSearch
            allowClear
            placeholder="All Groups"
            style={{ width: 220, flexShrink: 0 }}
            options={(Array.isArray(playerGroups) ? playerGroups : []).map((group) => ({
              label: group.name,
              value: group.player_group_id,
            }))}
            onChange={(value) => {setFilters({...filters, groups: value})}}   
            mode="multiple"
            maxTagCount="responsive"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={filters?.groups || []}
          />

      </div>

      <PermissionViewable 
        permissionCheck="ViewTreatmentLogs"
      >
        {/* Pie Charts Row */}
        <Row gutter={[16, 16]} style={{ margin: '20px 0' }}>
          <Col span={8}>
            <PieChart
              showDataLabelLines={showChartLabels}
              showDataLabels={showChartLabels}
              showLegend={false}
              size={showChartLabels ? '60%' : '90%'}
              options={{
                title: {
                  text: 'Treatments',
                  style: {
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }
                },
                series: [{
                  name: 'Count',
                  data: calculateTreatmentDistribution(applyFilters(allTreatmentLogs), treatment_types)
                }],
                tooltip: {
                  pointFormat: '{series.name}: <b>{point.y}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>'
                }
              }}
              height="300px"
            />
          </Col>
          <Col span={8}>
            <PieChart
              showDataLabelLines={showChartLabels}
              showDataLabels={showChartLabels}
              showLegend={false}
              size={showChartLabels ? '60%' : '90%'}
              options={{
                title: {
                  text: 'Locations',
                  style: {
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }
                },
                series: [{
                  name: 'Count',
                  data: calculateLocationDistribution(applyFilters(allTreatmentLogs), info?.team_colors?.all || [])
                }],
                tooltip: {
                  pointFormat: '{series.name}: <b>{point.y}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>'
                }
              }}
              height="300px"
            />
          </Col>
          <Col span={8}>
            <PieChart
              showDataLabelLines={showChartLabels}
              showDataLabels={showChartLabels}
              showLegend={false}
              size={showChartLabels ? '60%' : '90%'}
              options={{
                title: {
                  text: 'When Applied',
                  style: {
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }
                },
                series: [{
                  name: 'Count',
                  data: calculateTreatmentTimeDistribution(applyFilters(allTreatmentLogs), info?.team_colors?.all || [])
                }],
                tooltip: {
                  pointFormat: '{series.name}: <b>{point.y}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>'
                }
              }}
              height="300px"
            />
          </Col>
        </Row>
        
        {/* Chart Labels Control */}
        <div style={{ margin: '-10px 0 16px 0', textAlign: 'center' }}>
          <Checkbox 
            checked={showChartLabels}
            onChange={(e) => setShowChartLabels(e.target.checked)}
            style={{ 
              color: '#888888',
              '--ant-primary-color': '#888888'
            }}
          >
            <span style={{ color: '#888888' }}>Show Chart Labels</span>
          </Checkbox>
        </div>
      </PermissionViewable>


        <Table
            className="custom-table-container health-compact-table"
            scroll={{ x: 'max-content' }}
            rowClassName={(record) => {
              if (record.key === 'summary') {
                return "treatment-table-row treatment-summary-row";
              }
              return "treatment-table-row";
            }}
            size="small"
            columns={columns}
            dataSource={genTreatmentTree(allTreatmentLogs || [])}
            locale={!userPermissions?.ViewTreatmentLogs ? <Empty description="You do not have permission to view this table." /> :null }
            onChange={(_, filters) => {
              setFilters((prev) => ({
                ...prev,
                treatments: (filters?.formatted_treatment_list || []).filter(Boolean),
                locations: (filters?.formatted_locations || []).filter(Boolean),
                treatment_application_time: (filters?.treatment_application_time || []).filter(Boolean),
              }));
            }}
            rowKey="key"
        />

        {
          teamName === "Las Vegas Raiders" ? (
            <div className="health-table-actions">
              <Button
                type="primary"
                onClick={() => exportToCsv(
                  "treatment_logs.csv", 
                  applyFilters(allTreatmentLogs), 
                  columns.filter((column) => [
                    "date", 
                    "player", 
                    "treatment_count",
                    "formatted_treatment_list",
                    "formatted_locations",
                    "treatment_application_time",
                    "notes"
                  ].includes(column.dataIndex))
                )}
              >
                Download as CSV
              </Button>
            </div>
          ) : null }

      </PermissionViewable>
    </div>
  );
};
